import { useEffect, useRef, useState } from 'react';
import { TNullable } from '@common/types';
import { IMPROVED_HEADING_OFFSET } from '@constants/standard-pdf';
import { Document, Page } from 'react-pdf';
import { TChapterInfo, TPageDimensions } from './pdf.models';
import 'react-pdf/dist/esm/Page/TextLayer.css';

interface IPDFDocumentProps {
  handlePageRenderSuccess: ({ width, height }: TPageDimensions) => void;
  originalPageWidth: number;
  originalPageHeight: number;
  onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
  pageDimensions: TPageDimensions;
  scale: number;
  url: string;
  selectedChapter: TNullable<TChapterInfo>;
  selectedPage: number;
}

function PDFDocument(props: IPDFDocumentProps) {
  const [isFirstTime, setFirstTime] = useState<boolean>(true);
  const activeChapterRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeChapterRef.current) {
      activeChapterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [props.selectedChapter, activeChapterRef]);

  if (!props.selectedChapter) return null;

  function handleSuccessRender({ width, height }: TPageDimensions) {
    props.handlePageRenderSuccess({ width, height });
    setTimeout(() => {
      if (activeChapterRef.current && isFirstTime) {
        activeChapterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFirstTime(false);
      }
    }, 1000);
  }

  const scaleX = parseFloat((props.pageDimensions.width / props.originalPageWidth).toFixed(2));
  const scaleY = parseFloat((props.pageDimensions.height / props.originalPageHeight).toFixed(2));

  const activeChapter = {
    ...props.selectedChapter,
    coordinates: {
      x: (props.selectedChapter.coordinates.x + IMPROVED_HEADING_OFFSET.X) * scaleX,
      y: (props.selectedChapter.coordinates.y + IMPROVED_HEADING_OFFSET.Y) * scaleY,
    },
    width: (props.selectedChapter.width + IMPROVED_HEADING_OFFSET.WIDTH) * scaleX,
    height: (props.selectedChapter.height + IMPROVED_HEADING_OFFSET.HEIGHT) * scaleY,
  };

  return (
    <section className="pdf-document relative mt-4">
      <Document file={props.url} onLoadSuccess={props.onDocumentLoadSuccess}>
        <Page
          pageNumber={props.selectedPage}
          renderTextLayer={true}
          renderAnnotationLayer={false}
          scale={props.scale}
          onRenderSuccess={handleSuccessRender}
        />
        {activeChapter.page === props.selectedPage && (
          <div
            ref={activeChapterRef}
            className="highlight absolute bg-xircle-main/25 rounded-md border-b-2 border-xircle-main"
            style={{
              top: activeChapter.coordinates.y,
              left: activeChapter.coordinates.x,
              width: activeChapter.width,
              height: activeChapter.height,
            }}
          />
        )}
      </Document>
    </section>
  );
}

export default PDFDocument;
